import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { shipmentMissingService } from '../../services/shipmentMissingService';
import Alert from '../common/Alert';
import { AlertTriangle, Plus, Eye, Pencil } from 'lucide-react';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const MissingList = () => {
  const navigate = useNavigate();
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({ search: '', status: '' });

  useEffect(() => {
    fetchMissing();
  }, [page, filterValues.status]);

  const fetchMissing = async () => {
    try {
      setLoading(true);
      const filters = filterValues.status ? { status: filterValues.status } : {};
      const response = await shipmentMissingService.getAllMissing(page, 10, filters);

      const missingData = response.data.missing || response.data.data || [];
      setMissing(missingData);

      const pagination = response.data.pagination || {};
      setTotalPages(pagination.pages || 1);
    } catch (error) {
      console.error('Error fetching missing:', error);
      Alert.error(error.response?.data?.error || 'Failed to fetch missing shipments');
      setMissing([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredMissing = missing.filter((m) => {
    const searchLower = filterValues.search.toLowerCase();
    if (!searchLower) return true;
    return (
      m.missing_id?.toLowerCase().includes(searchLower) ||
      m.shipment_id?.toLowerCase().includes(searchLower) ||
      m.created_by?.toLowerCase().includes(searchLower)
    );
  });

  const columns = [
    { key: 'missing_id', label: 'Missing ID' },
    {
      key: 'shipment_id',
      label: 'Shipment ID',
      render: (m) => (
        <button
          onClick={() => navigate(`/shipments/view/${m.shipment_id}`)}
          className="font-medium text-brand hover:underline"
        >
          {m.shipment_id}
        </button>
      ),
    },
    {
      key: 'reason',
      label: 'Reason',
      render: (m) => (
        <span className="truncate block max-w-xs" title={m.reason}>
          {m.reason?.substring(0, 50)}
          {m.reason?.length > 50 ? '...' : ''}
        </span>
      ),
    },
    {
      key: 'approval_status',
      label: 'Status',
      render: (m) => <StatusBadge status={m.approval_status} />,
    },
    { key: 'created_by', label: 'Created By', render: (m) => m.created_by || 'N/A' },
    {
      key: 'created_at',
      label: 'Date',
      render: (m) =>
        m.created_at
          ? new Date(m.created_at).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : 'N/A',
    },
  ];

  const getActions = (m) => {
    const actions = [
      {
        label: 'View Details',
        icon: Eye,
        onClick: () => navigate(`/missing/view/${m.missing_id}`),
      },
    ];

    if (m.approval_status === 'pending') {
      actions.push({
        label: 'Review',
        icon: Pencil,
        onClick: () => navigate(`/missing/edit/${m.missing_id}`),
      });
    }

    return actions;
  };

  return (
    <PageLayout
      title="Missing Shipments"
      icon={AlertTriangle}
      actions={
        <Button icon={Plus} onClick={() => navigate('/missing/add')}>
          Report Missing
        </Button>
      }
    >
      <FilterPanel
        filters={[
          { key: 'search', label: 'Search', placeholder: 'Missing ID, shipment ID...' },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
            ],
          },
        ]}
        values={filterValues}
        onChange={(values) => {
          setFilterValues(values);
          setPage(1);
        }}
        onClear={() => {
          setFilterValues({ search: '', status: '' });
          setPage(1);
        }}
      />

      <DataTable
        columns={columns}
        data={filteredMissing}
        loading={loading}
        emptyMessage="No missing shipments found"
        rowKey="id"
        actions={getActions}
      />

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
    </PageLayout>
  );
};

export default MissingList;
